// src/components/attestations/AttestationList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchAttestations,
  deleteAttestation,
  syncAttestation,
  updateAttestationStatus,
  bulkSyncAttestations,
  exportAttestations,
  clearAttestationError,
  clearAttestationSuccess,
  setAttestationFilters,
} from '../../store/actions/attestationActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import FilterBar from '../common/FilterBar';

const AttestationList = () => {
  const dispatch = useDispatch();
  const { attestations, loading, error, success, meta, filters } = useSelector(
    (state) => state.attestations
  );
  const { agencies } = useSelector((state) => state.agencies);

  const [selectedAttestation, setSelectedAttestation] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkSyncConfirm, setShowBulkSyncConfirm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  
  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'ACTIVE', label: 'Actif', color: '#10b981' },
    { value: 'PENDING', label: 'En attente', color: '#f59e0b' },
    { value: 'CANCELLED', label: 'Annulé', color: '#ef4444' },
    { value: 'EXPIRED', label: 'Expiré', color: '#6b7280' },
    { value: 'ARCHIVED', label: 'Archivé', color: '#9ca3af' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    exportButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    exportButtonHover: {
      backgroundColor: '#059669',
    },
    bulkSyncButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#8b5cf6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    bulkSyncButtonHover: {
      backgroundColor: '#7c3aed',
    },
    bulkSyncButtonDisabled: {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
    filterBar: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellCheckbox: {
      padding: '0.75rem 1.5rem',
      width: '40px',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellCheckbox: {
      padding: '1rem 1.5rem',
      width: '40px',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    attestationInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    attestationNumber: {
      fontWeight: 600,
      color: '#2563eb',
      textDecoration: 'none',
    },
    attestationPolice: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    clientName: {
      fontWeight: 500,
      color: '#111827',
    },
    clientDetails: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    pendingBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    cancelledBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    expiredBadge: {
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
    },
    archivedBadge: {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    syncBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      fontSize: '0.7rem',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      borderRadius: '9999px',
    },
    syncSuccess: {
      color: '#10b981',
    },
    agencyInfo: {
      fontSize: '0.875rem',
    },
    agencyName: {
      fontWeight: 500,
      color: '#111827',
    },
    agencyRole: {
      fontSize: '0.7rem',
      color: '#6b7280',
    },
    dates: {
      fontSize: '0.875rem',
    },
    dateItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    amounts: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    primeNet: {
      color: '#111827',
    },
    primeTTC: {
      color: '#059669',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    statusButton: {
      color: '#f59e0b',
    },
    statusButtonHover: {
      color: '#d97706',
    },
    syncButton: {
      color: '#8b5cf6',
    },
    syncButtonHover: {
      color: '#7c3aed',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    statusModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    statusModalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '90%',
    },
    statusModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    statusOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      marginBottom: '2rem',
    },
    statusOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    statusOptionHover: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    statusOptionSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    statusOptionRadio: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    statusOptionLabel: {
      flex: 1,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    statusOptionBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'ACTIVE':
        return styles.activeBadge;
      case 'PENDING':
        return styles.pendingBadge;
      case 'CANCELLED':
        return styles.cancelledBadge;
      case 'EXPIRED':
        return styles.expiredBadge;
      case 'ARCHIVED':
        return styles.archivedBadge;
      default:
        return {};
    }
  };

  const fetchAttestationsData = useCallback(() => {
    dispatch(fetchAttestations(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      console.log(agencies);
      
      fetchAttestationsData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchAttestationsData();
      }
    }
  }, [fetchAttestationsData, filters]);

  useEffect(() => {
    return () => {
      dispatch(clearAttestationError());
      dispatch(clearAttestationSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    dispatch(setAttestationFilters({
      ...filters,
      search: searchTerm,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch(setAttestationFilters({
      ...filters,
      ...newFilters,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handlePageChange = useCallback((page) => {
    dispatch(setAttestationFilters({
      ...filters,
      page,
    }));
  }, [dispatch, filters]);

  const handleDeleteClick = useCallback((attestation) => {
    setSelectedAttestation(attestation);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteAttestation(selectedAttestation.id));
      setShowDeleteConfirm(false);
      setSelectedAttestation(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedAttestation]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedAttestation(null);
  }, []);

  const handleStatusClick = useCallback((attestation) => {
    setSelectedAttestation(attestation);
    setStatusFilter(attestation.status);
    setShowStatusModal(true);
  }, []);

  const handleStatusUpdate = useCallback(async () => {
    try {
      await dispatch(updateAttestationStatus(selectedAttestation.id, statusFilter));
      setShowStatusModal(false);
      setSelectedAttestation(null);
      setStatusFilter('');
    } catch (error) {
      console.error('Status update failed:', error);
    }
  }, [dispatch, selectedAttestation, statusFilter]);

  const handleSync = useCallback(async (attestation) => {
    try {
      await dispatch(syncAttestation(attestation.id));
    } catch (error) {
      console.error('Sync failed:', error);
    }
  }, [dispatch]);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(attestations.map(att => att.id));
    } else {
      setSelectedIds([]);
    }
  }, [attestations]);

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleBulkSyncClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setShowBulkSyncConfirm(true);
    }
  }, [selectedIds]);

  const handleBulkSyncConfirm = useCallback(async () => {
    try {
      await dispatch(bulkSyncAttestations(selectedIds));
      setShowBulkSyncConfirm(false);
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk sync failed:', error);
    }
  }, [dispatch, selectedIds]);

  const handleExport = useCallback(async () => {
    try {
      await dispatch(exportAttestations('csv', filters));
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, [dispatch, filters]);

  if (loading && !attestations.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des attestations</h1>
        <div style={styles.headerActions}>
          <button
            onClick={handleExport}
            style={styles.exportButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.exportButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.exportButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exporter
          </button>
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkSyncClick}
              style={{
                ...styles.bulkSyncButton,
                ...(selectedIds.length === 0 ? styles.bulkSyncButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkSyncButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkSyncButton.backgroundColor;
                }
              }}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Synchroniser ({selectedIds.length})
            </button>
          )}
          <Link
            to="/admin/attestations/form"
            style={styles.createButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouvelle attestation
          </Link>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearAttestationError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearAttestationSuccess())}
        />
      )}

      <div style={styles.filterBar}>
        <FilterBar
          filters={[
            {
              type: 'select',
              name: 'status',
              label: 'Statut',
              options: statusOptions,
            },
            {
              type: 'select',
              name: 'senderId',
              label: 'Agence émettrice',
              options: [
                { value: '', label: 'Toutes les agences' },
                ...agencies.map(a => ({ value: a.id, label: a.name })),
              ],
            },
            {
              type: 'select',
              name: 'receiverId',
              label: 'Agence réceptrice',
              options: [
                { value: '', label: 'Toutes les agences' },
                ...agencies.map(a => ({ value: a.id, label: a.name })),
              ],
            },
            {
              type: 'dateRange',
              name: 'dateRange',
              label: 'Période',
            },
          ]}
          onFilterChange={handleFilterChange}
          initialValues={filters}
        />
      </div>

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher par nom, numéro d'attestation, numéro de police..." 
          fullWidth
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCellCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === attestations.length && attestations.length > 0}
                  onChange={handleSelectAll}
                  style={styles.checkbox}
                />
              </th>
              <th style={styles.tableHeaderCell}>Attestation</th>
              <th style={styles.tableHeaderCell}>Client</th>
              <th style={styles.tableHeaderCell}>Statut</th>
              <th style={styles.tableHeaderCell}>Expéditeur</th>
              <th style={styles.tableHeaderCell}>Destinataire</th>
              <th style={styles.tableHeaderCell}>Dates</th>
              <th style={styles.tableHeaderCell}>Montants</th>
              <th style={styles.tableHeaderCell}>Sync</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attestations.map((attestation) => (
              <tr 
                key={attestation.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCellCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(attestation.id)}
                    onChange={() => handleSelectOne(attestation.id)}
                    style={styles.checkbox}
                  />
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.attestationInfo}>
                    <Link to={`/admin/attestations/${attestation.id}/details`} style={styles.attestationNumber}>
                      {attestation.numAttestation || 'N/A'}
                    </Link>
                    <div style={styles.attestationPolice}>
                      Police: {attestation.numPolice || 'N/A'}
                    </div>
                    {attestation.numEmission && (
                      <div style={styles.attestationPolice}>
                        Émission: {attestation.numEmission}
                      </div>
                    )}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div>
                    <div style={styles.clientName}>
                      {attestation.firstName} {attestation.lastName}
                    </div>
                    {attestation.dateOfBirth && (
                      <div style={styles.clientDetails}>
                        Né(e) le: {new Date(attestation.dateOfBirth).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span
                    style={{
                      ...styles.badge,
                      ...getStatusStyle(attestation.status),
                    }}
                  >
                    {statusOptions.find(s => s.value === attestation.status)?.label || attestation.status}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  {attestation.SenderAgency ? (
                    <div style={styles.agencyInfo}>
                      <div style={styles.agencyName}>{attestation.SenderAgency.name}</div>
                      <div style={styles.agencyRole}>Émetteur</div>
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.tableCell}>
                  {attestation.ReceiverAgency ? (
                    <div style={styles.agencyInfo}>
                      <div style={styles.agencyName}>{attestation.ReceiverAgency.name}</div>
                      <div style={styles.agencyRole}>Récepteur</div>
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.dates}>
                    {attestation.DateEffet && (
                      <div style={styles.dateItem}>
                        <span>📅 Effet: {new Date(attestation.DateEffet).toLocaleDateString()}</span>
                      </div>
                    )}
                    {attestation.DateEcheance && (
                      <div style={styles.dateItem}>
                        <span>⏰ Échéance: {new Date(attestation.DateEcheance).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.amounts}>
                    {attestation.primeNet !== null && (
                      <div style={styles.primeNet}>
                        Net: {attestation.primeNet.toLocaleString()} FCFA
                      </div>
                    )}
                    {attestation.PrimeTTC !== null && (
                      <div style={styles.primeTTC}>
                        TTC: {attestation.PrimeTTC.toLocaleString()} FCFA
                      </div>
                    )}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.syncBadge}>
                    <span style={attestation.synched ? styles.syncSuccess : {}}>
                      {attestation.synched ? '✅' : '⏳'}
                    </span>
                    {attestation.synched && attestation.synchedAt && (
                      <span style={{ fontSize: '0.6rem' }}>
                        {new Date(attestation.synchedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/attestations/${attestation.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="Voir les détails"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/attestations/${attestation.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Modifier"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleStatusClick(attestation)}
                      style={{
                        ...styles.actionButton,
                        ...styles.statusButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.statusButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.statusButton.color;
                      }}
                      title="Changer le statut"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleSync(attestation)}
                      style={{
                        ...styles.actionButton,
                        ...styles.syncButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.syncButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.syncButton.color;
                      }}
                      title="Synchroniser"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleDeleteClick(attestation)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Supprimer"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {attestations.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucune attestation trouvée correspondant à vos critères
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div style={styles.statusModal} onClick={() => setShowStatusModal(false)}>
          <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.statusModalTitle}>
              Changer le statut de l'attestation
            </h3>
            <div style={styles.statusOptions}>
              {statusOptions.filter(s => s.value).map((option) => (
                <div
                  key={option.value}
                  style={{
                    ...styles.statusOption,
                    ...(statusFilter === option.value ? styles.statusOptionSelected : {}),
                  }}
                  onMouseEnter={(e) => {
                    if (statusFilter !== option.value) {
                      e.currentTarget.style.borderColor = styles.statusOptionHover.borderColor;
                      e.currentTarget.style.backgroundColor = styles.statusOptionHover.backgroundColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (statusFilter !== option.value) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => setStatusFilter(option.value)}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={statusFilter === option.value}
                    onChange={() => setStatusFilter(option.value)}
                    style={styles.statusOptionRadio}
                  />
                  <span style={styles.statusOptionLabel}>{option.label}</span>
                  <span
                    style={{
                      ...styles.statusOptionBadge,
                      ...getStatusStyle(option.value),
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleStatusUpdate}
                style={{
                  ...styles.saveButton,
                  ...(loading ? styles.saveButtonDisabled : {}),
                }}
              >
                {loading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'attestation"
        message={`Êtes-vous sûr de vouloir supprimer l'attestation "${selectedAttestation?.numAttestation}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Bulk Sync Confirmation */}
      <ConfirmDialog
        isOpen={showBulkSyncConfirm}
        onClose={() => setShowBulkSyncConfirm(false)}
        onConfirm={handleBulkSyncConfirm}
        title="Synchroniser plusieurs attestations"
        message={`Êtes-vous sûr de vouloir synchroniser ${selectedIds.length} attestation(s) ?`}
        confirmText="Synchroniser"
        cancelText="Annuler"
        type="info"
      />
    </div>
  );
};

export default AttestationList;